import { campaignKey } from "@/campaigns";
import { useCampaignNavigation } from "@/contexts/campaign-navigation";
import { useImageUrl } from "@/hooks/use-image-url";
import { useWidget } from "@/hooks/widget";
import { useAppStore } from "@/stores";
import type { PublicCampaignSchema } from "@/types";

/**
 * What a toast needs from the campaign it is about: its art, and a tap that
 * opens the widget (a no-op when already open) on that campaign. `onClick` is
 * left out until the `campaigns` stream knows the campaign — notifications can
 * arrive first (see `rekeyWonNotifications` for the same race on the win
 * queue), and there is nothing to route to before then.
 */
export function useToastCampaign(
  campaignId: number | undefined,
  onDismiss: () => void
): { imageUrl: string | undefined; onClick: (() => void) | undefined } {
  // A store reference (or undefined), so the selector is `===`-stable across
  // writes that don't touch this campaign.
  const campaign = useAppStore((state) =>
    Object.values(state.campaignsById).find(
      (candidate: PublicCampaignSchema) => candidate.id === campaignId
    )
  );
  const imageUrl = useImageUrl(campaign?.image);
  const { open: openWidget } = useWidget();
  const navigateToCampaign = useCampaignNavigation();

  const onClick = campaign
    ? () => {
        onDismiss();
        // Opening first: its handler resets the route to Home synchronously,
        // so selecting and navigating after it is what makes the page stick.
        openWidget();
        navigateToCampaign(campaignKey(campaign));
      }
    : undefined;

  return { imageUrl, onClick };
}
